/**
 * Onboarding parties — dynamic form types (customers, partners, …).
 * Vendors / employees / workspace stay on their own APIs.
 */

export const PARTY_RESERVED_TYPES = new Set([
  'vendors',
  'employees',
  'workspace',
  'customers',
])

export const isOnboardingPartyType = (type) => {
  if (!type) return false
  return !PARTY_RESERVED_TYPES.has(String(type).toLowerCase())
}

export const ONBOARDING_PARTY_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Invited', value: 'invited' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'In Review', value: 'in_review' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
]

const PARTY_STATUS_LABELS = Object.fromEntries(
  ONBOARDING_PARTY_STATUS_OPTIONS.map((o) => [o.value, o.label]),
)

export const partyStatusLabel = (status) => {
  if (!status) return 'Unknown'
  return PARTY_STATUS_LABELS[status] ?? String(status).replace(/_/g, ' ')
}

/** Human title for a form type slug, e.g. customers → Customers */
export const partyTypeTitle = (type, fallbackName) => {
  if (fallbackName) return fallbackName
  if (!type) return 'Onboarding'
  return String(type)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export const INACTIVE_LINK_MESSAGES = {
  expired: 'This onboarding link has expired.',
  used: 'This onboarding link has already been used.',
  revoked: 'This onboarding link has been revoked.',
  inactive: 'This onboarding link is no longer active.',
}

export const inactiveLinkMessage = (statusType) =>
  INACTIVE_LINK_MESSAGES[statusType] ??
  'This onboarding link is no longer active.'

/** Shared onboarding link on GET /onboarding-parties list envelope. */
export const resolvePartySharedUrl = (data) => {
  const raw = data?.shared_url
  if (!raw) return ''
  if (typeof raw === 'string') return raw
  return raw.link_url || ''
}

/** Per-party invite / onboarding URL from list or detail payloads. */
export const resolvePartyInviteLink = (party = {}) => {
  if (typeof party.link_url === 'string' && party.link_url.trim()) {
    return party.link_url.trim()
  }
  const nested = party.link ?? party.onboarding_link ?? party.latest_link
  if (typeof nested === 'string' && nested.trim()) return nested.trim()
  if (nested && typeof nested === 'object') {
    const url = nested.link_url || nested.url
    if (typeof url === 'string' && url.trim()) return url.trim()
  }
  const links = party.links
  if (Array.isArray(links) && links.length) {
    const active = links.find((l) => l?.link_url) ?? links[0]
    if (active?.link_url) return String(active.link_url).trim()
  }
  return ''
}

/**
 * Local draft persistence for in-progress onboarding party forms
 * (admin create/edit + public token submit). Keyed per-context so drafts
 * never leak between different parties/types/tokens sharing a browser.
 */
const PARTY_DRAFT_PREFIX = 'complyone-onboarding-party-draft:'

export const buildPartyDraftKey = (parts = []) =>
  PARTY_DRAFT_PREFIX + parts.filter(Boolean).join(':')

export const loadPartyDraft = (key) => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.answers)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const savePartyDraft = (key, draft) => {
  try {
    localStorage.setItem(key, JSON.stringify(draft))
    return true
  } catch {
    return false
  }
}

export const clearPartyDraft = (key) => {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore — nothing to clean up if storage is unavailable
  }
}

/** Empty check used by draft + AI-fill (leave filled fields alone). */
export const isPartyAnswerEmpty = (answer) => {
  if (answer === null || answer === undefined) return true
  if (typeof answer === 'string') return answer.trim() === ''
  if (Array.isArray(answer)) return answer.length === 0
  if (typeof answer === 'object') {
    if ('full_address' in answer || 'city' in answer) {
      return (
        !String(answer.full_address || '').trim() &&
        !String(answer.city || '').trim()
      )
    }
    return Object.keys(answer).length === 0
  }
  return false
}

export const collectUploadedFilesFromAnswers = (answers = []) => {
  const files = []
  for (const item of answers) {
    if (item?.type !== 'file') continue
    const raw = item.answer
    const list = Array.isArray(raw) ? raw : raw ? [raw] : []
    for (const file of list) {
      if (file instanceof File) {
        files.push({ question_id: item.question_id, file })
      }
    }
  }
  return files
}

/** Stable id of current File uploads — skip re-calling AI for the same set. */
export const buildPartyFileFingerprint = (answers = []) =>
  collectUploadedFilesFromAnswers(answers)
    .map(
      ({ question_id, file }) =>
        `${question_id}:${file.name}:${file.size}:${file.lastModified}`,
    )
    .sort()
    .join('|')

export const stepHasUploadedFiles = (stepQuestions = [], formAnswers = []) =>
  stepQuestions.some((q) => {
    if (q.type !== 'file') return false
    const answer = formAnswers[q._index]?.answer
    const list = Array.isArray(answer) ? answer : answer ? [answer] : []
    return list.some((f) => f instanceof File)
  })

/** Payload for answer-from-file (multipart: file|files[], type, persist). */
export const buildAnswerFromFilePayload = ({
  answers = [],
  type,
  persist = false,
} = {}) => {
  const formData = new FormData()
  const files = collectUploadedFilesFromAnswers(answers).map(({ file }) => file)

  if (files.length === 1) {
    formData.append('file', files[0])
  } else {
    files.forEach((file) => formData.append('files[]', file))
  }

  if (type) formData.append('type', String(type))
  if (persist) formData.append('persist', '1')

  return formData
}

/**
 * Prefill invitee name/email from the onboarding link (or party) into
 * matching empty questionnaire answers — e.g. "Your full name" /
 * "Your work email address". Never overwrites a filled answer.
 * @returns {number} fields filled
 */
export const applyPartyInviteeDetails = (
  formAnswers = [],
  { name, email } = {},
) => {
  let filledCount = 0
  const nameVal = typeof name === 'string' ? name.trim() : ''
  const emailVal = typeof email === 'string' ? email.trim() : ''
  const titleOf = (a) => String(a.question_title || a.title || '')

  if (nameVal) {
    const target = formAnswers.find(
      (a) =>
        a.type === 'text' &&
        isPartyAnswerEmpty(a.answer) &&
        /your\s+full\s+name/i.test(titleOf(a)),
    )
    if (target) {
      target.answer = nameVal
      filledCount++
    }
  }

  if (emailVal) {
    const target = formAnswers.find(
      (a) =>
        a.type === 'email' &&
        isPartyAnswerEmpty(a.answer) &&
        /your\s+(work\s+)?email/i.test(titleOf(a)),
    )
    if (target) {
      target.answer = emailVal
      filledCount++
    }
  }

  return filledCount
}

/** True when an answer item is a persisted upload (has a fetchable URL / name). */
export const isPartyStoredFile = (file) => {
  if (!file || typeof file !== 'object' || file instanceof File) return false
  return !!(
    file.url ||
    file.file_path ||
    file.path ||
    file.original_name ||
    file.file_name
  )
}

/** Normalize API file answers into a stable array of stored-file objects. */
export const normalizePartyFileAnswer = (answer) => {
  if (answer == null || answer === '') return null
  const list = Array.isArray(answer) ? answer : [answer]
  const stored = list.filter((f) => isPartyStoredFile(f))
  return stored.length ? stored : null
}

/**
 * Snapshot current file-field answers so a multipart draft/submit cannot
 * leave the in-form uploader empty if the live array is touched mid-request.
 */
export const snapshotPartyFileAnswers = (formAnswers = []) =>
  formAnswers.map((a) => (a?.type === 'file' ? a.answer : undefined))

/**
 * Restore file answers wiped during a draft round-trip. Prefer keeping any
 * File still present; only fill back from the snapshot when the field is empty.
 */
export const restorePartyFileAnswers = (formAnswers = [], snapshot = []) => {
  formAnswers.forEach((item, index) => {
    if (item?.type !== 'file') return
    const saved = snapshot[index]
    if (saved == null) return

    const current = item.answer
    const list = Array.isArray(current) ? current : current ? [current] : []
    const hasFile = list.some((f) => f instanceof File)
    const hasStored = list.some((f) => isPartyStoredFile(f))
    if (hasFile || hasStored) return

    item.answer = saved
  })
}

/**
 * After a successful draft/submit — or on page load — apply server-persisted
 * file answers (objects with url/file_path/original_name) onto the form.
 * Never overwrites a field that still has a live File upload.
 * @returns {number} file fields updated
 */
export const applyPartySavedFiles = (formAnswers = [], data) => {
  let updated = 0

  const applyOne = (questionId, value) => {
    if (questionId == null || value == null) return
    const stored = normalizePartyFileAnswer(value)
    if (!stored) return

    const id = Number(questionId)
    const target = formAnswers.find(
      (a) =>
        a.question_id === questionId ||
        a.question_id === id ||
        a.id === questionId ||
        a.id === id,
    )
    if (!target || target.type !== 'file') return

    const current = Array.isArray(target.answer)
      ? target.answer
      : target.answer
        ? [target.answer]
        : []
    // Keep in-progress browser File uploads; only fill empty / URL-only slots.
    if (current.some((f) => f instanceof File)) return

    target.answer = stored
    updated++
  }

  const answers = data?.answers
  if (Array.isArray(answers)) {
    answers.forEach((item) => {
      applyOne(
        item.workspace_form_question_id ?? item.question_id ?? item.id,
        item.answer,
      )
    })
    return updated
  }

  const sections = data?.party?.sections ?? data?.sections
  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      ;(section.questions || []).forEach((q) => {
        applyOne(q.id ?? q.question_id ?? q.workspace_form_question_id, q.answer)
      })
    })
  }

  return updated
}

/**
 * Apply AI suggestions onto form answers.
 * By default only fills empty non-file fields — never overwrites user input.
 * Pass `overwrite: true` when the extraction was triggered by the user
 * deliberately (re-)uploading a source document — in that case the new
 * answers should replace whatever was previously filled from an older file.
 * Response shape: { answers: [{ workspace_form_question_id, answer, ... }] }
 * @returns {number} fields filled
 */
export const applyPartyAiAnswers = (formAnswers = [], data, { overwrite = false } = {}) => {
  let filledCount = 0

  const applyOne = (questionId, value) => {
    if (questionId == null || value == null || isPartyAnswerEmpty(value)) return
    const id = Number(questionId)
    const target = formAnswers.find(
      (a) =>
        a.question_id === questionId ||
        a.question_id === id ||
        a.id === questionId ||
        a.id === id,
    )
    if (!target || target.type === 'file') return
    if (!overwrite && !isPartyAnswerEmpty(target.answer)) return

    target.answer = Array.isArray(value) ? [...value] : value
    filledCount++
  }

  const answers = data?.answers
  if (Array.isArray(answers)) {
    answers.forEach((item) => {
      applyOne(
        item.workspace_form_question_id ?? item.question_id ?? item.id,
        item.answer,
      )
    })
    return filledCount
  }

  const sections = data?.party?.sections ?? data?.sections
  if (Array.isArray(sections) && sections.length) {
    sections.forEach((section) => {
      ;(section.questions || []).forEach((q) => {
        applyOne(q.id ?? q.question_id ?? q.workspace_form_question_id, q.answer)
      })
    })
  }

  return filledCount
}

/** Normalize file answers for preview (File objects + stored uploads). */
export const getPartyQuestionFiles = (answer) => {
  const list = Array.isArray(answer) ? answer : answer ? [answer] : []
  return list
    .filter(
      (f) =>
        f instanceof File ||
        f?.url ||
        f?.file_path ||
        f?.original_name ||
        f?.name,
    )
    .map((f) => {
      if (f instanceof File) {
        return {
          name: f.name,
          original_name: f.name,
          size: f.size,
        }
      }
      return f
    })
}

/** Human-readable answer for preview (non-file types). */
export const formatPartyAnswerDisplay = (answer, type) => {
  if (isPartyAnswerEmpty(answer)) return ''
  if (type === 'address') {
    return (
      answer?.full_address ||
      [answer?.street, answer?.city, answer?.state, answer?.country]
        .filter(Boolean)
        .join(', ') ||
      ''
    )
  }
  if (type === 'checkbox' || type === 'country_list') {
    return Array.isArray(answer)
      ? answer.filter(Boolean).join(', ')
      : String(answer)
  }
  if (Array.isArray(answer)) return answer.filter(Boolean).join(', ')
  if (typeof answer === 'object') return JSON.stringify(answer)
  return String(answer)
}

/**
 * Build read-only preview sections from the live form (before reset).
 * Only includes questions that are visible given current answers.
 */
export const buildPartyPreviewSections = (
  processedSections = [],
  formAnswers = [],
  { isVisible = () => true } = {},
) =>
  processedSections
    .map((section) => ({
      id: section.id ?? section.title,
      title: section.title || 'Details',
      description: section.description || '',
      questions: (section.questions || [])
        .filter((q) => isVisible(q))
        .map((q) => {
          const answer = formAnswers[q._index]?.answer ?? null
          return {
            id: q.id ?? q._index,
            title: q.title,
            type: q.type,
            answer,
            is_required: q.is_required,
          }
        }),
    }))
    .filter((section) => section.questions.length > 0)

